import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fileUrl, asArray } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import useReveal from "../hooks/useReveal";
import { ArrowLeft, ArrowRight } from "lucide-react";

export function NewsPage() {
    const { t, pick, isRTL } = useI18n();
    const ref = useReveal();
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get("/news?limit=100").then((r) => setItems(asArray(r.data, "news"))).catch(() => {});
    }, []);

    return (
        <div ref={ref} data-testid="news-page">
            <section className="bg-[#EFF0F1] py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <div className="section-eyebrow mb-5 plus-mark">
                        {t("section_news_overline")}
                    </div>
                    <h1 className="heading page-hero-oneline">
                        {t("section_news_title")}
                    </h1>
                </div>
            </section>

            <section className="bg-white py-20">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {items.map((n, i) => (
                        <Link key={n.id} to={`/news/${n.slug}`} className="group reveal bg-[#EFF0F1] hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`news-item-${i}`}>
                            <div className="aspect-[16/9] overflow-hidden bg-white">
                                {n.cover_image && (
                                    <img
                                        src={fileUrl(n.cover_image)}
                                        alt={pick(n, "title")}
                                        loading={i < 3 ? "eager" : "lazy"}
                                        fetchPriority={i === 0 ? "high" : "auto"}
                                        decoding="async"
                                        className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-105"
                                    />
                                )}
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="text-xs uppercase tracking-[0.25em] text-[#0060A7] mb-3">
                                    {pick(n, "category")}
                                </div>
                                <h2 className="heading text-lg md:text-xl tracking-tight leading-tight mb-3 font-bold">{pick(n, "title")}</h2>
                                <p className="text-[#374151] leading-relaxed">{pick(n, "excerpt")}</p>
                                <div className="text-xs text-[#6B7280] mt-4">
                                    {new Date(n.published_at).toLocaleDateString(isRTL ? "ar" : "en")}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}

export function NewsDetailPage() {
    const { slug } = useParams();
    const { t, pick, isRTL } = useI18n();
    const Arrow = isRTL ? ArrowLeft : ArrowRight;
    const ref = useReveal();
    const [n, setN] = useState(null);
    const [nf, setNf] = useState(false);

    useEffect(() => {
        setN(null); setNf(false);
        api.get(`/news/${slug}`).then((r) => setN(r.data)).catch(() => setNf(true));
    }, [slug]);

    if (nf) return <div className="min-h-[60vh] flex items-center justify-center"><p className="heading text-3xl">{t("not_found")}</p></div>;
    if (!n) return <div className="py-40 text-center text-[#6B7280]">…</div>;

    return (
        <div ref={ref} data-testid="news-detail-page">
            <section className="bg-[#EFF0F1] py-20 md:py-28">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
                    <div className="section-eyebrow mb-5 plus-mark">
                        {pick(n, "category")} � {new Date(n.published_at).toLocaleDateString(isRTL ? "ar" : "en")}
                    </div>
                    <h1 className="heading page-hero-oneline max-w-4xl reveal">
                        {pick(n, "title")}
                    </h1>
                </div>
            </section>

            {n.cover_image && (
                <section className="bg-white pt-12 md:pt-16">
                    <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
                        <div className="aspect-[16/9] bg-[#EFF0F1] overflow-hidden">
                            <img
                                src={fileUrl(n.cover_image)}
                                alt={pick(n, "title")}
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                                className="h-full w-full object-contain"
                            />
                        </div>
                    </div>
                </section>
            )}
            <section className="bg-white py-20">
                <div className="max-w-[820px] mx-auto px-6 lg:px-12">
                    <div className="text-xl md:text-2xl leading-relaxed text-[#1f2937] whitespace-pre-wrap" data-testid="news-body">
                        {pick(n, "body")}
                    </div>
                    <Link to="/news" className="legends-link inline-flex items-center gap-3 mt-12 heading text-xl">
                        <Arrow size={18} /> {t("cta_view_all")}
                    </Link>
                </div>
            </section>
        </div>
    );
}
